import { memo, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Modal,
  Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import SelectOptions from "../../components/ui/SelectOptions";
import eligibilityCheckServices from "../../services/api/service/eligibilityCheckService";
import BasicDatePicker from "../../components/ui/BasicDatePicker";
import dayjs from "dayjs";

const formatDateAndTime = (value: string) => {
  return new Date(value).toISOString().split("T")[0];
};

type Options = {
  value: string;
  label: string;
};

const consentTypeOptions: Options[] = [
  { value: "one_time", label: "One Time" },
  { value: "periodic", label: "Periodic" },
];

const CONSENT_DURATION_UNIT = "DAY";
const PERIODIC_LOOKBACK_DAYS = 180;
const ONE_TIME_LOOKBACK_DAYS = 365;
const PERIODIC_MAX_FUTURE_YEARS =1825;
const ONE_TIME_LOOKBACK_MONTH = 1;
const ONE_TIME_LOOKBACK_MONTH_VAL = "MONTH";

const getPeriodicDefaults = () => ({
  fromDate: dayjs().subtract(PERIODIC_LOOKBACK_DAYS, "day").format("YYYY-MM-DD"),
  toDate: dayjs().add(1825, "day").format("YYYY-MM-DD"),
});

const getOneTimeDefaults = () => ({
  fromDate: dayjs().subtract(ONE_TIME_LOOKBACK_DAYS, "day").format("YYYY-MM-DD"),
  toDate: dayjs().format("YYYY-MM-DD"),
});

const getConsentDefaults = (consentType: string) =>
  consentType === "one_time" ? getOneTimeDefaults() : getPeriodicDefaults();

const getDerivedConsentDurationValue = (fromDate: string, toDate: string) => {
  const from = dayjs();
  const to = dayjs(toDate);

  if (!from.isValid() || !to.isValid()) {
    return "0";
  }

  return String(Math.max(to.startOf("day").diff(from.startOf("day"), "day"), 0));
};

interface Props {
  isAccountAggregatorModalOpen: boolean;
  setIsAccountAggregatorModalOpen: (value: boolean) => void;
  applicationNumber: string;
  tabIndex: number;
  applicants: string[];
}

const AccountAggregatorModal = memo(
  ({
    isAccountAggregatorModalOpen,
    setIsAccountAggregatorModalOpen,
    applicationNumber,
    tabIndex,
    applicants,
  }: Props) => {
    const [consentType, setConsentType] = useState<string>(consentTypeOptions[1]?.value);
    const [aggregatorLoader, setAggregatorLoader] = useState<boolean>(false);
    const [fetchLoader, setFetchLoader] = useState<boolean>(true);
    const [hasApiDateRange, setHasApiDateRange] = useState<boolean>(false);
    // Prevents consentType useEffect from overwriting API-loaded dates
    const skipConsentTypeEffect = useRef(false);

    const [fromDate, setFromDate] = useState<string>(getPeriodicDefaults().fromDate);
    const [toDate, setToDate] = useState<string>(getPeriodicDefaults().toDate);
    const [fromDateError, setFromDateError] = useState<string>("");
    const [toDateError, setToDateError] = useState<string>("");

    // Date validation function
    const validateDate = (
      dateValue: string,
      setError: (error: string) => void,
      dateType: "from" | "to",
      otherDateValue?: string,
    ) => {
      // Only validate complete dates in YYYY-MM-DD format
      if (!dateValue || dateValue.length !== 10) {
        setError("");
        return false;
      }

      // Validate date format using regex
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(dateValue)) {
        setError("");
        return false;
      }

      const selectedDate = dayjs(dateValue);

      // Check if date is valid
      if (!selectedDate.isValid()) {
        setError("");
        return false;
      }

      const today = dayjs().startOf("day");
      const oneTimeMinFromDate = today.subtract(ONE_TIME_LOOKBACK_DAYS, "day");
      const periodicMaxToDate = today.add(PERIODIC_MAX_FUTURE_YEARS, "day");

      if (dateType === "from" && consentType === "periodic") {
        const expectedFromDate = getPeriodicDefaults().fromDate;
        if (dateValue !== expectedFromDate) {
          const defaults = getConsentDefaults(consentType);
          setFromDate(defaults.fromDate);
          setToDate(defaults.toDate);
          setFromDateError("");
          setToDateError("");
          setError("");
          return true;
        }
      }

      if (dateType === "to" && consentType === "periodic") {
        if (selectedDate.isBefore(today, "day")) {
          const defaults = getConsentDefaults(consentType);
          setFromDate(defaults.fromDate);
          setToDate(defaults.toDate);
          setFromDateError("");
          setToDateError("");
          setError("");
          return true;
        }

        if (selectedDate.isAfter(periodicMaxToDate, "day")) {
          setError("To Date cannot be later than 1825 days from today");
          return false;
        }
      }

      if (consentType === "one_time" && dateType === "from") {
        if (selectedDate.isBefore(oneTimeMinFromDate, "day")) {
          const defaults = getConsentDefaults(consentType);
          setFromDate(defaults.fromDate);
          setToDate(defaults.toDate);
          setFromDateError("");
          setToDateError("");
          setError("");
          return true;
        }

        if (selectedDate.isAfter(today, "day")) {
          setError("From Date cannot be later than today");
          return false;
        }
      }

      if (consentType === "one_time" && dateType === "to") {
        if (selectedDate.isBefore(oneTimeMinFromDate, "day")) {
          const defaults = getConsentDefaults(consentType);
          setFromDate(defaults.fromDate);
          setToDate(defaults.toDate);
          setFromDateError("");
          setToDateError("");
          setError("");
          return true;
        }

        if (selectedDate.isAfter(today, "day")) {
          setError("To Date cannot be later than today");
          return false;
        }
      }

      // Additional validation for To Date
      if (dateType === "to") {
        const fromDateToUse = otherDateValue || fromDate;
        if (fromDateToUse && fromDateToUse.length === 10) {
          const fromDateObj = dayjs(fromDateToUse);

          // Skip if fromDate is invalid
          if (!fromDateObj.isValid()) {
            setError("");
            return true;
          }

          if (selectedDate.isBefore(fromDateObj, "day")) {
            setError("To Date cannot be earlier than From Date");
            return false;
          }
        }
      }

      // Additional validation for From Date when To Date exists
      if (dateType === "from") {
        const toDateToUse = otherDateValue || toDate;
        if (toDateToUse && toDateToUse.length === 10) {
          const toDateObj = dayjs(toDateToUse);

          // Skip if toDate is invalid
          if (!toDateObj.isValid()) {
            setError("");
            return true;
          }

          if (selectedDate.isAfter(toDateObj, "day")) {
            setError("From Date cannot be later than To Date");
            return false;
          }
        }
      }

      setError("");
      return true;
    };

    const handleAccountAggregatorRequest = async () => {
      const isFromDateValid = validateDate(fromDate, setFromDateError, "from");
      const isToDateValid = validateDate(toDate, setToDateError, "to");
      if (!isFromDateValid || !isToDateValid) {
        return; 
      }

      const consentDurationValue = getDerivedConsentDurationValue(fromDate, toDate);

      try {
        setAggregatorLoader(true);

        const applicant = applicants?.find(
          (val) => tabIndex === Number(val.match(/\d+/)?.[0] || 0),
        );

        const payload = {
          trackingId: applicationNumber,
          coApplicantType: tabIndex === 0 ? applicants[0] : applicant,
          consentType: consentType,
          consentDuration: consentType==="one_time" ? ONE_TIME_LOOKBACK_MONTH_VAL : CONSENT_DURATION_UNIT,
          consentDurationValue:consentType==="one_time" ? String(ONE_TIME_LOOKBACK_MONTH) : consentDurationValue,
          fromDate: fromDate + "T00:00:00Z",
          toDate: toDate + "T00:00:00Z",
        };

        const response = await eligibilityCheckServices.saveConsentDuration(payload);
        if (response?.status == 200) {
          setIsAccountAggregatorModalOpen(false);
        }
      } catch (error: any) {
        console.log("Error while saving consent duration data", error);
      } finally {
        setAggregatorLoader(false);
      }
    };

    const fetchConsentDurationData = async () => {
      try {
        setFetchLoader(true);
        const response =
          await eligibilityCheckServices.getConsentDurationData(
            applicationNumber,
          );
        if (response?.status == 200 && response?.data?.status === "success") {
          const data = {
            applicant: response?.data?.data.applicant,
            coApplicants: response?.data?.data.coApplicants,
            trackingId: response?.data?.data?.trackingId,
          };
          const {
            fromDate,
            toDate,
            consentType: fetchedConsentType,
            hasApiDateRange: apiDateRangeAvailable,
          } =
            getInitialFromAndToData(data);
          setFromDate(fromDate);
          setToDate(toDate);
          setHasApiDateRange(apiDateRangeAvailable);

          if (fetchedConsentType) {
            skipConsentTypeEffect.current = true;
            setConsentType(fetchedConsentType);
          }
        } else {
          setHasApiDateRange(false);
        }
      } catch (error: any) {
        console.log("Error while getting consent duration data", error);
        setHasApiDateRange(false);
      } finally {
        setFetchLoader(false);
      }
    };

    function getInitialFromAndToData(consentDurationData: any) {
      let consentType = consentTypeOptions[1]?.value;
      let { fromDate, toDate } = getConsentDefaults(consentType);
      let hasApiDateRange = false;

      if (Object.keys(consentDurationData)?.length) {
        if (tabIndex === 0) {
          const apiFromDate = consentDurationData.applicant?.aaFromDate;
          const apiToDate = consentDurationData.applicant?.aaToDate;
          hasApiDateRange = Boolean(apiFromDate && apiToDate);
          consentType = consentDurationData.applicant?.aaConsentType || consentType;
          const defaults = getConsentDefaults(consentType);

          // Get FROM and TO date from applicant
          fromDate = apiFromDate
            ? formatDateAndTime(apiFromDate)
            : defaults.fromDate;
          toDate = apiToDate
            ? formatDateAndTime(apiToDate)
            : defaults.toDate;
        } else if (tabIndex > 0) {
          // Get FROM and TO date from coApplicants
          const coApplicant = consentDurationData?.coApplicants?.find(
            (item: any) => {
              if (
                tabIndex === parseInt(item?.coApplicantType.match(/\d+/)[0])
              ) {
                return item;
              }
            },
          );
          const apiFromDate = coApplicant?.aaFromDate;
          const apiToDate = coApplicant?.aaToDate;
          hasApiDateRange = Boolean(apiFromDate && apiToDate);
          consentType = coApplicant?.aaConsentType || coApplicant?.consentType || consentType;
          const defaults = getConsentDefaults(consentType);
          fromDate = apiFromDate
            ? formatDateAndTime(apiFromDate)
            : defaults.fromDate;

          toDate = apiToDate
            ? formatDateAndTime(apiToDate)
            : defaults.toDate;
        }
      }
      return {
        fromDate,
        toDate,
        consentType,
        hasApiDateRange,
      };
    }

    useEffect(() => {
      fetchConsentDurationData();
    }, []);

    useEffect(() => {
      if (skipConsentTypeEffect.current) {
        skipConsentTypeEffect.current = false;
        return;
      }
      if (hasApiDateRange) {
        setFromDateError("");
        setToDateError("");
        return;
      }
      const defaults = getConsentDefaults(consentType);
      setFromDate(defaults.fromDate);
      setToDate(defaults.toDate);
      setFromDateError("");
      setToDateError("");
    }, [consentType, hasApiDateRange]);

    return (
      <Box>
        {/* Account Aggregator Modal */}
        <Modal
          open={isAccountAggregatorModalOpen}
          onClose={() => setIsAccountAggregatorModalOpen(false)}
          aria-labelledby="account-aggregator-modal-title"
          aria-describedby="account-aggregator-modal-description"
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "90%", sm: "400px" },
              bgcolor: "background.paper",
              borderRadius: "12px",
              boxShadow: 24,
              p: 4,
              outline: "none",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Typography
                id="account-aggregator-modal-title"
                variant="h6"
                component="h2"
                sx={{
                  fontWeight: 600,
                  fontSize: "18px",
                  color: "#1F2233",
                }}
              >
                Share via Account Aggregator
              </Typography>
              <Button
                onClick={() => setIsAccountAggregatorModalOpen(false)}
                sx={{
                  minWidth: "auto",
                  p: 0.5,
                  color: "#666",
                }}
              >
                <Close />
              </Button>
            </Box>

            {fetchLoader ? (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 6 }}>
                <CircularProgress size={32} sx={{ color: "#00b3b5" }} />
              </Box>
            ) : (<>

            <Box sx={{ mb: 2 }}>
              <SelectOptions
                options={consentTypeOptions}
                label="Consent Type"
                value={consentType}
                isMandatory={false}
                onChange={(val) => {
                  const nextConsentType = val.toString();
                  const defaults = getConsentDefaults(nextConsentType);
                  setHasApiDateRange(false);
                  setConsentType(nextConsentType);
                  setFromDate(defaults.fromDate);
                  setToDate(defaults.toDate);
                  setFromDateError("");
                  setToDateError("");
                }}
              />
            </Box>

            {/* From Date Picker */}
            <BasicDatePicker
              label="From Date"
              value={fromDate ? dayjs(fromDate) : null}
              disabled={consentType === "periodic"}
              onChange={(date) => {
                // If date is null (cleared) or invalid, set to empty string
                const formattedDate = date && dayjs(date).isValid() ? dayjs(date).format("YYYY-MM-DD") : "";
                setFromDate(formattedDate);

                // Clear errors immediately when user starts changing the date
                if (!formattedDate || formattedDate.length !== 10) {
                  setFromDateError("");
                }
              }}
              onBlur={() => {
                if (fromDate && fromDate.length === 10) {
                  validateDate(fromDate, setFromDateError, "from");
                  // Cross-validate 'to' date if it exists
                  if (toDate) validateDate(toDate, setToDateError, "to");
                } else if (fromDate) {
                  setFromDateError("Please enter a valid date");
                }
              }}
              minDate={consentType === "one_time" ? dayjs().subtract(ONE_TIME_LOOKBACK_DAYS, "day") : undefined}
              maxDate={consentType === "one_time" ? dayjs() : undefined}
              error={!!fromDateError}
              helperText={fromDateError}
            />

            <Box sx={{ mb: 1 }} />

            {/* To Date Picker */}
            <BasicDatePicker
              label="To Date"
              value={toDate ? dayjs(toDate) : null}
              disabled={false}
              onChange={(date) => {
                const formattedDate = date && dayjs(date).isValid() ? dayjs(date).format("YYYY-MM-DD") : "";
                setToDate(formattedDate);

                if (!formattedDate || formattedDate.length !== 10) {
                  setToDateError("");
                }
              }}
              onBlur={() => {
                if (toDate && toDate.length === 10) {
                  validateDate(toDate, setToDateError, "to");
                  // Cross-validate 'from' date if it exists
                  if (fromDate) validateDate(fromDate, setFromDateError, "from");
                } else if (toDate) {
                  setToDateError("Please enter a valid date");
                }
              }}
              minDate={
                consentType === "periodic"
                  ? dayjs()
                  : dayjs().subtract(ONE_TIME_LOOKBACK_DAYS, "day")
              }
              maxDate={consentType === "periodic" ? dayjs().add(PERIODIC_MAX_FUTURE_YEARS, "day") : dayjs()}
              error={!!toDateError}
              helperText={toDateError}
            />

            <Box sx={{ mb: 3 }} />    

            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "flex-end",
              }}
            >
              <Button
                variant="outlined"
                onClick={() => setIsAccountAggregatorModalOpen(false)}
                sx={{
                  textTransform: "none",
                  borderColor: "#ddd",
                  color: "#666",
                  "&:hover": {
                    borderColor: "#ccc",
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleAccountAggregatorRequest}
                disabled={aggregatorLoader}
                sx={{
                  textTransform: "none",
                  backgroundColor: "#00b3b5",
                  color: "#fff",
                  "&:hover": {
                    backgroundColor: "#00999a",
                  },
                  "&:disabled": {
                    backgroundColor: "#f5f5f5",
                    color: "#999",
                  },
                }}
                startIcon={aggregatorLoader && <CircularProgress size={16} />}
              >
                {aggregatorLoader ? "Saving..." : "Save"}
              </Button>
            </Box>
            </>)}
          </Box>
        </Modal>
      </Box>
    );
  },
);

export default AccountAggregatorModal;
